import { FC, useState } from "react";
import { motion } from "framer-motion";
import { FaUser, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaEuroSign } from "react-icons/fa";
import { PiUsersFourFill } from "react-icons/pi";
import { IoTimeSharp } from "react-icons/io5";
import { MdAccessTimeFilled } from "react-icons/md";
import { RiQrScanFill } from "react-icons/ri";
import { HiOutlineLocationMarker } from "react-icons/hi";

export interface ReservationDetailedCardProps {
  className?: string;
  defaultOpen?: boolean;
  data: any;
  totalCount?: number;
}
const ReservationsDetailedCard: FC<ReservationDetailedCardProps> = ({
  className = "",
  data,
  totalCount,
  defaultOpen = false,
}) => {
  function formatDateShort(dateString: any) {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const renderDetailTop = () => {
    return (
      <div>
        <div className="flex flex-col justify-center md:flex-row">
          <div className="flex my-5 md:my-0">
            <div className="ml-4 space-y-10 text-sm">
              <div className="flex flex-col space-y-1">
                <span className="font-semibold text-neutral-950">
                  <div className="flex space-x-2">
                    <FaUser className="w-4 h-4 dark:text-white" />
                    <span className="dark:text-white">Client:</span>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      {data?.Client?.name} {data?.Client?.lastName}
                    </span>
                  </div>
                </span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="font-semibold text-neutral-950">
                  <div className="flex space-x-2">
                    <PiUsersFourFill className="w-5 h-5 dark:text-white" />
                    <span className="dark:text-white">Personnes autorisées:</span>
                    <span className="text-neutral-500 dark:text-neutral-400">{data?.visitor}</span>
                  </div>
                </span>
              </div>
            </div>
          </div>
          <div className="border-l border-neutral-200 dark:border-neutral-700 md:mx-6 lg:mx-10"></div>
          <div className="ml-4 space-y-10 text-sm">
            <div className="space-y-1">
              <span className="font-semibold text-neutral-950">
                <div className="flex space-x-2">
                  <IoTimeSharp className="w-5 h-5 dark:text-white" />
                  <span className="dark:text-white">Heure d'entrée:</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{data?.startHour}</span>
                </div>
              </span>
            </div>
            <div className="space-y-1">
              <span className="font-semibold text-neutral-950">
                <div className="flex space-x-2">
                  <MdAccessTimeFilled className="w-5 h-5 dark:text-white" />
                  <span className="dark:text-white">Heure de sortie:</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{data?.endHour}</span>
                </div>
              </span>
            </div>
            <div className="flex flex-col space-y-1">
              <span className="font-semibold text-neutral-950 dark:text-white ">
                <div className="flex space-x-2 ">
                  <RiQrScanFill className="w-5 h-5 dark:text-white " />
                  <span className="dark:text-white">Code de réservation:</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{data?.reference}</span>
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDetail = () => {
    if (!isOpen) return null;
    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="mx-6 mb-6"
      >
        <div className="bg-gradient-to-r from-white/80 to-blue-50/80 dark:from-neutral-800/80 dark:to-blue-900/20 rounded-2xl p-6 border border-neutral-200/50 dark:border-neutral-600/50 backdrop-blur-sm">
          <h4 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center space-x-2">
            <FaUser className="w-5 h-5 text-blue-500" />
            <span>Détails de la réservation</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <FaUser className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Client</p>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {data?.Client?.name} {data?.Client?.lastName}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <PiUsersFourFill className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Personnes autorisées</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{data?.visitor}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <IoTimeSharp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Heure d'entrée</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{data?.startHour}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <MdAccessTimeFilled className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Heure de sortie</p>
                  <p className="font-medium text-neutral-900 dark:text-white">{data?.endHour}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-neutral-200/50 dark:border-neutral-600/50">
            <div className="flex items-center justify-center space-x-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-xl p-4">
              <RiQrScanFill className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div className="text-center">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">Code de réservation</p>
                <p className="font-mono font-bold text-lg text-neutral-900 dark:text-white">{data?.reference}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
        className={`overflow-hidden relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 dark:from-neutral-900 dark:via-blue-900/10 dark:to-purple-900/10 rounded-3xl border border-neutral-200/50 dark:border-neutral-700/50 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm ${className}`}
        data-nc-id="FlightCard"
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img
                  src={`${process.env.REACT_APP_CLOUD_FRONT}${data?.Location?.images[0]}`}
                  className="w-20 h-20 rounded-2xl object-cover shadow-md"
                  alt={data?.Location?.title}
                />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <HiOutlineLocationMarker className="w-3 h-3 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                  {data?.Location?.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-neutral-600 dark:text-neutral-300">
                  <div className="flex items-center space-x-1">
                    <FaCalendarAlt className="w-4 h-4 text-blue-500" />
                    <span>{formatDateShort(data?.selectedDate)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FaEuroSign className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{data?.price} €</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  data.status === "En cours"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : data.status === "Annulée"
                      ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                }`}
              >
                {data?.status}
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`w-10 h-10 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-600 rounded-full flex items-center justify-center cursor-pointer shadow-md hover:shadow-lg transition-all duration-200 ${
                  isOpen ? "transform rotate-180" : ""
                }`}
                onClick={() => setIsOpen(!isOpen)}
              >
                <i className="text-lg las la-angle-down text-neutral-600 dark:text-neutral-300"></i>
              </motion.button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/50 dark:bg-neutral-800/50 rounded-2xl p-4 border border-neutral-200/50 dark:border-neutral-600/50">
              <div className="flex items-center space-x-2 mb-2">
                <FaClock className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Horaires</span>
              </div>
              <p className="text-neutral-900 dark:text-white font-semibold">
                {data?.startHour} - {data?.endHour}
              </p>
            </div>

            <div className="bg-white/50 dark:bg-neutral-800/50 rounded-2xl p-4 border border-neutral-200/50 dark:border-neutral-600/50">
              <div className="flex items-center space-x-2 mb-2">
                <PiUsersFourFill className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Visiteurs</span>
              </div>
              <p className="text-neutral-900 dark:text-white font-semibold">{data?.visitor} personnes</p>
            </div>

            <div className="bg-white/50 dark:bg-neutral-800/50 rounded-2xl p-4 border border-neutral-200/50 dark:border-neutral-600/50">
              <div className="flex items-center space-x-2 mb-2">
                <RiQrScanFill className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Référence</span>
              </div>
              <p className="text-neutral-900 dark:text-white font-semibold text-sm">{data?.reference}</p>
            </div>
          </div>
        </div>
        {renderDetail()}
      </motion.div>
    </>
  );
};

export default ReservationsDetailedCard;
